/* eslint-disable no-return-assign */
import React from 'react';
import { hasValue } from 'swipeclock-frontend';
import { InputErrorText, InputHintText } from './Styles';
// eslint-disable-next-line import/no-extraneous-dependencies
import { NumericFormat, PatternFormat } from 'react-number-format';
import FormControl from '@mui/material/FormControl';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';

/**
 * NumberTextfield is an extension of the uncontrolled Textfield component which allows
 * different number formatting options such as currency, integer and float.
 *
 * @param {Object} props - Props passed down from parent component
 * @param {string} props.error - Error text to display
 * @param {string} props.formatType - Type of number format ('currency', 'integer', 'float')
 * @param {boolean} [props.gutter] - Whether to display a gutter below the text field.
 * @param {string} props.hint - Hint text to display
 * @param {string} props.id - Unique ID for the input
 * @param {object} props.inputProps - Additional props to pass to the base input component
 * @param {string} props.label - Label text for the input
 * @param {function} [props.onBlur] - Blur event handler for the input
 * @param {function} [props.onChange] - Change event handler for the input
 * @param {boolean} [props.required] - Whether the input is required
 * @param {function} props.validationRef - The ref for keeping track of wizard/form validation.
 * @returns {JSX.Element} A formatted input field based on the specified number format.
 */
export default function NumberTextfield({
  error,
  formatType,
  gutter,
  hint,
  id,
  inputProps,
  label,
  required,
  validationRef,
  ...props
}) {
  // Custom props for NumericFormat  based on formatType
  let formatProps = {};
  let isPattern = false;
  if (formatType === 'phone') {
    isPattern = true;
  }
  switch (formatType) {
    case 'currency':
      formatProps = {
        prefix: '$',
        decimalScale: 2,
        fixedDecimalScale: true,
        allowNegative: false,
      };
      break;
    case 'integer':
      formatProps = {
        allowNegative: false,
        decimalScale: 0,
      };
      break;
    case 'float':
      formatProps = {
        decimalScale: 2,
        allowNegative: false,
      };
      break;
    case 'percent':
      formatProps = {
        suffix: '%',
        decimalScale: 2,
        fixedDecimalScale: true,
        allowNegative: false,
      };
      break;
    case 'phone':
      formatProps = {
        format: '( ### ) ### - ####',
        allowEmptyFormatting: true,
        mask: '_',
      };
      break;
    case 'zipcode':
      formatProps = {
        format: '#####', // Only 5 digits
      };
      break;
    default:
      break;
  }

  return (
    <FormControl fullWidth {...props}>
      {isPattern && (
        <PatternFormat
          customInput={TextField}
          id={id}
          inputProps={{
            maxLength: props?.maxLength > 0 ? props.maxLength : null,
            ...inputProps,
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputRef={(el) => (validationRef.current[id] = el)}
          label={label}
          name={id}
          variant="outlined"
          required={required}
          valueIsNumericString
          {...formatProps}
          {...props}
        />
      )}
      {!isPattern && (
        <NumericFormat
          customInput={TextField}
          id={id}
          inputProps={{
            maxLength: props?.maxLength > 0 ? props.maxLength : null,
            ...inputProps,
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputRef={(el) => (validationRef.current[id] = el)}
          label={label}
          name={id}
          variant="outlined"
          required={required}
          valueIsNumericString
          {...formatProps}
          {...props}
        />
      )}
      {error && <InputErrorText>{error}</InputErrorText>}
      {hint && !hasValue(error) && <InputHintText>{hint}</InputHintText>}
      {gutter && !hasValue(error) && !hasValue(hint) && (
        <InputHintText>&nbsp;</InputHintText>
      )}
    </FormControl>
  );
}

NumberTextfield.propTypes = {
  error: PropTypes.string,
  formatType: PropTypes.oneOf([
    'currency',
    'integer',
    'float',
    'percent',
    'phone',
    'zipcode',
  ]).isRequired,
  gutter: PropTypes.bool,
  hint: PropTypes.string,
  id: PropTypes.string.isRequired,
  inputProps: PropTypes.object,
  label: PropTypes.string.isRequired,
  maxLength: PropTypes.number,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  validationRef: PropTypes.any.isRequired,
};
